<template>
    <div class="container-fluid account-catchlight">
        <div class="account-catchlight__header">
            <img
                :src="`/wp-content/themes/snappy-theme/assets/images/catchlight.svg`"
                alt="Catchlight"
                class="account-catchlight__logo"
            />

            <div class="account-catchlight__title">Catchlight</div>

            <div class="account-catchlight__subtitle">Organic Growth Platform for Wealth Management</div>

            <button :disabled="connecting || loading || storing" class="btn btn-lg btn-primary"
                    @click="handleConnectClick">
                {{ isConnected ? 'Disconnect' : 'Connect' }}
            </button>

            <a v-if="this.fleetCatchlightSignupLink" class="account-catchlight__sign-up"
               :href="this.fleetCatchlightSignupLink">
                Don't have a Catchlight account? Sign up!
            </a>
        </div>

        <div class="account-catchlight-box">
            <div class="account-catchlight-box__header">
                Enable Catchlight
                <div class="account-catchlight-box__switch">
                    <snappy-switch :disabled="updating" :value="isEnabled" @input="toggleCatchlight"></snappy-switch>
                </div>
            </div>

            <div class="account-catchlight-box__text">
                <strong>Discover which of your contacts are more likely to convert with Catchlight.</strong> With this setting enabled, "Enrich with Catchlight" buttons will appear throughout the Contacts and Groups interfaces for you to quickly score leads and find additional unique insights about your contacts including their estimated investable assets, income range, projected client revenue, and more.
            </div>
        </div>
    </div>
</template>

<script>
import crypto from 'crypto-js';
import SnappyBadge from '../components/SnappyBadge.vue';
import SnappySwitch from '../components/SnappySwitch.vue';
import {getEnvironmentVars} from '../utility/api/open/getEnvironmentVars';

export default {
    name: 'account-catchlight-page',

    components: {
        SnappyBadge,
        SnappySwitch
    },

    data() {
        return {
            accessToken: null,
            expiresAt: null,
            isEnabled: false,
            isConnected: false,
            code_challenge: null,
            loading: false,
            connecting: false,
            updating: false,
            storing: false,
            params: {}
        }
    },

    async mounted() {
        let envVars = await getEnvironmentVars();

        window.CATCHLIGHT_CLIENT_ID = envVars.catchlight_client_id;
        window.CATCHLIGHT_OAUTH_AUTHORIZE_URL = envVars.catchlight_oauth_authorize_url;
        window.CATCHLIGHT_OAUTH_TOKEN_URL = envVars.catchlight_oauth_token_url;

        this.getCatchlight();

        this.params = Object.fromEntries(
            new URLSearchParams(window.location.search)
        )

        if (this.params.code && this.params.state && localStorage.getItem('sk_catchlight_state') && this.params.state === JSON.parse(localStorage.getItem('sk_catchlight_state')).state) {
            await this.getToken(this.params.code)

            if (localStorage.getItem('sk_catchlight_state') && JSON.parse(localStorage.getItem('sk_catchlight_state')).redirect_url) {
                window.location.href = JSON.parse(localStorage.getItem('sk_catchlight_state')).redirect_url
                localStorage.removeItem('sk_catchlight_state');
                return;
            }
        } else {
            this.getChallenge();
        }
    },

    methods: {
        handleConnectClick() {
            this.isConnected ? this.deleteCatchlight() : this.connect()
        },

        async connect() {
            const AUTHORIZE_URL_PARAMS = {
                redirect_uri: window.location.href,
                client_id: window.CATCHLIGHT_CLIENT_ID,
                state: JSON.parse(localStorage.getItem('sk_catchlight_state')).state,
                code_challenge: this.code_challenge,
                response_type: 'code',
                scope: 'openid',
                prompt: 'consent',
                access_type: 'offline',
                code_challenge_method: 'S256',
            }

            const AUTHORIZE_URL = `${window.CATCHLIGHT_OAUTH_AUTHORIZE_URL}?${new URLSearchParams(AUTHORIZE_URL_PARAMS).toString()}`

            try {
                window.open(AUTHORIZE_URL, '_self');
            } catch (e) {
                console.log(e);
            }
        },

        async getCatchlight() {
            this.loading = true;

            try {
                const {data: {data}} = await axios.get('/api/?entity=vessel&action=getCatchlight')
                this.setData(data);
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },

        async storeCatchlight(token, expiresAt) {
            this.storing = true;

            try {
                const {data: {data}} = await axios.post('/api/?entity=vessel&action=storeCatchlight', {
                    access_token: token,
                    expires_at: expiresAt
                })
                this.setData(data);
            } catch (e) {
                console.log(e);
            } finally {
                this.storing = false;
            }
        },

        async toggleCatchlight() {
            this.updating = true;

            try {
                const {data: {data}} = await axios.post('/api/?entity=vessel&action=toggleCatchlight')
                this.setData(data);
            } catch (e) {
                this.connect()
            } finally {
                this.updating = false;
            }
        },

        async deleteCatchlight() {
            this.connecting = true;

            try {
                const {data: {data}} = await axios.delete('/api/?entity=vessel&action=deleteCatchlight')
                this.setData(data);
            } catch (e) {
                this.connect()
            } finally {
                this.connecting = false;
            }
        },

        async getChallenge() {
            const code_verifier = this.createRandomString(128);
            this.code_challenge = this.base64Url(crypto.SHA256(code_verifier));

            window.localStorage.setItem('sk_catchlight_state', JSON.stringify({
                state: this.createRandomString(40),
                redirect_url: this.params.redirect_url
            }));

            window.localStorage.setItem('sk_catchlight_code_verifier', code_verifier);
        },

        getToken(code) {
            this.connecting = true;

            const params = {
                grant_type: 'authorization_code',
                client_id: window.CATCHLIGHT_CLIENT_ID,
                redirect_uri: window.location.href,
                code_verifier: window.localStorage.getItem('sk_catchlight_code_verifier'),
                code
            }

            axios.post(window.CATCHLIGHT_OAUTH_TOKEN_URL, new URLSearchParams(params)).then(response => {
                this.storeCatchlight(response.data.access_token, response.data.expires_in)
            }).catch(error => {
                console.log(error);
            }).finally(() => {
                this.connecting = false;
                window.localStorage.removeItem('sk_catchlight_state');
                window.localStorage.removeItem('sk_catchlight_code_verifier');
            })
        },

        setData(data) {
            this.isConnected = data.is_connected
            this.isEnabled = data.is_enabled
        },

        createRandomString(num) {
            return [...Array(num)].map(() => Math.random().toString(36)[2]).join('')
        },

        base64Url(string) {
            return string.toString(crypto.enc.Base64)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');
        },
    },

    props: {
        fleetCatchlightSignupLink: String,
    },
}
</script>
<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.account-catchlight {
    a {
        text-decoration: none !important;
    }

    &__header {
        background: $white-color;
        min-height: 144px;
        padding: 16px;
        box-shadow: inset 0px -1px 0px $platinum-color;
        display: grid;
        grid-template-columns: 1fr min-content;
        grid-template-areas:
      'logo badge'
      'title title'
      'subtitle subtitle'
      'actions actions';
        @media (min-width: $breakpoint-md) {
            padding: 24px;
            grid-template-columns: 110px 1fr min-content;
            grid-template-rows: min-content min-content;
            grid-template-areas:
        'logo title badge'
        'logo subtitle actions';
        }
    }

    &__title,
    &__subtitle {
        text-align: left;
        font-weight: bold;
    }

    &__title {
        font-size: 32px;
        line-height: 52px;
        color: $mine-shaft-color;
        grid-area: title;
        @media (min-width: $breakpoint-md) {
            font-size: 48px;
        }
    }

    &__subtitle {
        font-size: 16px;
        align-self: flex-end;
        grid-area: subtitle;
        margin-bottom: 20px;
        @media (min-width: $breakpoint-md) {
            font-size: 20px;
            margin-bottom: 0;
        }
    }

    &__logo {
        width: 72px;
        height: 72px;
        grid-area: logo;
        @media (min-width: $breakpoint-md) {
            width: 96px;
            height: 96px;
        }
    }

    &__badge {
        text-align: right;
        grid-area: badge;
    }

    &__actions {
        display: inline-flex;
        align-items: flex-end;
        gap: 10px;
        grid-area: actions;
    }

    &__links {
        margin: 16px 0 8px;
        display: flex;
        gap: 16px;
    }

    &__link {
        flex: 1;
        font-weight: 700;
        display: block;
        margin-bottom: 8px;

        &--active {
            animation: blinker 1.5s linear infinite;
        }
    }

    &-box {
        min-height: 120px;
        padding: 24px;
        text-align: left;
        background: $white-color;
        border: 1px solid $platinum-color;
        border-radius: 8px;
        margin: 16px 0;

        &--disabled {
            filter: grayscale(1) brightness(0.7);
            pointer-events: none;
        }

        &__header {
            font-size: 24px;
            color: $mine-shaft-color;
            display: grid;
            gap: 20px;
            margin-bottom: 10px;
            @media (min-width: $breakpoint-md) {
                grid-template-columns: max-content 1fr max-content;
                grid-template-rows: 1fr;
                gap: 10px;
            }
        }

        &__switch {
            display: flex;
            align-items: center;
            gap: 30px;
        }

        &__title {
            font-weight: 700;
        }

        &__key {
            color: $mine-shaft-color;
        }

        &__text {
            color: $dove-grey-color;
        }

        &__date {
            background: $color-iceberg;
            font-size: 12px;
            padding: 8px 12px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
        }

        &__link {
            color: $half-baked-color;
            cursor: pointer;
        }
    }
}

@keyframes blinker {
    50% {
        opacity: 0.1;
    }
}

.account-catchlight__header {
    position: relative;

    .account-catchlight__sign-up {
        text-decoration: underline !important;
        position: absolute;
        top: 90px;
        right: 24px;
        color: #6d6d6d;
        font-size: 14px;
    }
}
</style>
